import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import { NewsletterSection } from "@/components/NewsletterForm";
import { PageIntro, FadeUp } from "@/components/Reveal";
import { Spark } from "@/components/Doodles";
import { useContent } from "@/context/ContentContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Check, Minus } from "lucide-react";

const PACKAGE_IDS = ["neighbor", "regular", "weirdo", "founding"];

const CHART_COLUMNS = ["BENEFIT", "NEIGHBOR", "REGULAR", "WEIRDO ⭐", "FOUNDING FREAK"];
const CHART_ROWS = [
    ["Monthly dues", "$10", "$25", "$50", "$100"],
    ["Member-only happenings", "yes", "yes", "yes", "yes"],
    ["Newsletter / member communications", "yes", "yes", "yes", "yes"],
    ["PCCH workshop + event discount", "10%", "10%", "20%", "20%"],
    ["Early registration for limited events", "no", "yes", "yes", "yes"],
    ["Free shared Work Room access (drop-in, no booking)", "yes", "yes", "yes", "yes"],
    ["Private Work Room reservation (whole room)", "$25/hr", "$20/hr", "$12.50/hr", "5 hrs included"],
    ["Private reservation discount vs. public", "no", "20%", "50%", "5 free hrs + 50% after"],
    ["Additional private reservation hours", "$25/hr", "$20/hr", "$12.50/hr", "$12.50/hr"],
    ["Household included", "no", "no", "no", "yes — whole household"],
    ["Mug on the mug shelf", "no", "no", "yes", "yes"],
    ["Pay-It-Forward eligible", "+$10", "+$10", "+$10", "+$10"],
    ["Initial membership cap", "None", "None", "None", "30"],
];

const STUDIO_RULES = [
    ["Shared member access", "Free with every paid membership — drop in, grab an open spot, first-come first-served"],
    ["What you're reserving", "The entire Work Room, exclusively, by the hour"],
    ["During your reservation", "The room is yours — calls, meetings, recording, and reasonable noise are fine"],
    ["Drop-in during a reservation", "Paused — shared use resumes when the booking ends"],
    ["Reservations open", "14 days ahead"],
    ["Founding Freak included time", "5 hours per month"],
    ["Rollover", "No — resets monthly"],
    ["Cancellation", "12+ hours ahead to retain included hour"],
    ["Work room public value", "$25/hr"],
    ["Value of 5 included hours", "$125/month"],
];

const Cell = ({ v }) => {
    if (v === "yes") return <Check className="mx-auto h-4 w-4 text-forest" aria-label="included" />;
    if (v === "no") return <Minus className="mx-auto h-4 w-4 text-ink/30" aria-label="not included" />;
    return <span>{v}</span>;
};

const TIER_ROTATIONS = ["lg:rotate-1", "lg:-rotate-[0.5deg]", "lg:-rotate-1", "lg:rotate-[0.5deg]"];

const Membership = () => {
    const { t } = useContent();
    const tiers = t("membership.tiers", []);
    const rates = t("membership.studio_rates", []);
    const [paying, setPaying] = useState(null);

    const checkout = async (packageId) => {
        setPaying(packageId);
        try {
            const { data } = await api.post("/payments/checkout", {
                package_id: packageId,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
            setPaying(null);
        }
    };

    return (
        <>
            <Seo
                title="Membership"
                description="Membership tiers at Panther City Clubhouse — keep the lights on, the coffee hot, and the weird stuff funded."
            />
            <PageIntro kicker="join the club(house)" title="MEMBERSHIP" intro={t("membership.intro", "")} />

            <section data-testid="membership-tiers" className="px-5 pb-16 sm:px-10">
                <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2">
                    {tiers.map((tier, i) => {
                        const popular = tier.badge === "MOST POPULAR";
                        const founding = tier.style === "founding";
                        const dark = popular || founding;
                        return (
                            <FadeUp key={tier.name} delay={i * 0.08}>
                                <div
                                    data-testid={`membership-tier-${i}`}
                                    className={`relative flex h-full flex-col border-2 border-ink p-8 hard-shadow-lg transition-transform duration-200 hover:-translate-y-1 ${TIER_ROTATIONS[i] || ""} ${
                                        popular ? "bg-orange text-cream" : founding ? "bg-forest text-cream" : "bg-paper text-ink"
                                    }`}
                                >
                                    {tier.badge && (
                                        <span className="absolute -top-4 right-6 border-2 border-ink bg-mustard px-3 py-1 font-display text-xs tracking-[0.15em] text-ink hard-shadow">
                                            {tier.badge}
                                        </span>
                                    )}
                                    <h2 className="font-display text-3xl tracking-wide">{tier.name}</h2>
                                    <p className={`mt-1 font-display text-5xl ${dark ? "text-mustard" : "text-forest"}`}>{tier.price}</p>
                                    <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-cream/90" : "text-ink/70"}`}>{tier.desc}</p>
                                    <ul className="mt-6 flex-1 space-y-3">
                                        {(tier.perks || []).map((perk) => (
                                            <li key={perk} className="flex items-start gap-2 text-sm font-medium">
                                                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-mustard" : "text-orange"}`} />
                                                {perk}
                                            </li>
                                        ))}
                                    </ul>
                                    {tier.note && (
                                        <p className={`mt-4 border-t-2 border-dashed pt-3 text-xs font-semibold ${founding ? "border-cream/40 text-mustard" : "border-ink/30 text-brick"}`}>
                                            {tier.note}
                                        </p>
                                    )}
                                    <button
                                        onClick={() => checkout(PACKAGE_IDS[i] || "regular")}
                                        disabled={paying !== null}
                                        data-testid={`membership-tier-cta-${i}`}
                                        className={`btn-stamp mt-8 w-full disabled:opacity-60 ${popular ? "!bg-cream !text-ink" : ""} ${founding ? "!bg-mustard !text-ink" : ""}`}
                                    >
                                        {paying === (PACKAGE_IDS[i] || "regular") ? "Heading to checkout…" : tier.cta || "Join"}
                                    </button>
                                </div>
                            </FadeUp>
                        );
                    })}
                </div>
            </section>

            <section id="studio" data-testid="membership-studio" className="border-y-2 border-ink bg-forest px-5 py-16 sm:px-10 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <FadeUp>
                        <p className="font-hand text-2xl text-mustard -rotate-2 sm:text-3xl">{t("membership.studio_heading", "YOUR SPACE, YOUR PACE.")}</p>
                        <p className="mt-4 max-w-3xl leading-relaxed text-cream/85">{t("membership.studio_body", "")}</p>
                        <Link to="/work-room" data-testid="membership-workroom-link" className="btn-stamp mt-6 inline-block !bg-mustard !text-ink">
                            See the Work Room →
                        </Link>
                    </FadeUp>
                    <p className="mt-10 font-display text-sm tracking-[0.15em] text-cream/60">EXCLUSIVE WHOLE-ROOM RESERVATION RATES</p>
                    <div className="mt-4 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                        {rates.map((r, i) => (
                            <FadeUp key={r.label} delay={i * 0.07}>
                                <div
                                    data-testid={`studio-rate-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    className={`h-full border-2 border-ink p-5 text-center hard-shadow ${i % 2 ? "rotate-1 bg-paper" : "-rotate-1 bg-paper"}`}
                                >
                                    <p className="font-display text-sm tracking-[0.15em] text-brick">{r.label.toUpperCase()}</p>
                                    <p className="mt-2 font-display text-2xl leading-tight tracking-wide text-ink">{r.rate}</p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>
                    <FadeUp delay={0.15}>
                        <p className="mt-8 max-w-3xl font-hand text-2xl leading-snug text-mustard -rotate-1">
                            {t("membership.studio_fineprint", "")}
                        </p>
                    </FadeUp>
                </div>
            </section>

            <section data-testid="benefit-chart-section" className="px-5 py-16 sm:px-10 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <FadeUp>
                        <p className="font-hand text-2xl text-orange -rotate-2 sm:text-3xl">the whole menu, side by side</p>
                        <h2 className="mt-1 font-display text-4xl tracking-wide text-ink sm:text-5xl">THE DETAILED BREAKDOWN</h2>
                    </FadeUp>
                    <FadeUp delay={0.1} className="mt-8">
                        <div className="overflow-x-auto border-2 border-ink hard-shadow-lg">
                            <table data-testid="benefit-chart" className="w-full min-w-[720px] bg-paper text-left text-sm">
                                <thead>
                                    <tr className="border-b-2 border-ink bg-mustard font-display tracking-wider text-ink">
                                        {CHART_COLUMNS.map((c) => (
                                            <th key={c} className="px-4 py-3 text-center first:text-left">{c}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {CHART_ROWS.map((row, ri) => (
                                        <tr key={row[0]} className={`border-b border-ink/15 ${ri % 2 ? "bg-cream/60" : ""}`}>
                                            <td className="px-4 py-3 font-semibold text-ink">{row[0]}</td>
                                            {row.slice(1).map((v, ci) => (
                                                <td key={ci} className="px-4 py-3 text-center text-ink/80">
                                                    <Cell v={v} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <section data-testid="studio-rules-section" className="border-y-2 border-ink bg-paper px-5 py-16 sm:px-10 sm:py-20">
                <div className="mx-auto max-w-4xl">
                    <FadeUp>
                        <h2 className="font-display text-3xl tracking-wide text-ink sm:text-4xl">WORK ROOM RESERVATION RULES</h2>
                        <p className="mt-2 font-hand text-2xl text-brick -rotate-1">short, fair, and mostly common sense</p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="mt-8">
                        <div className="border-2 border-ink bg-cream hard-shadow-lg">
                            <table data-testid="studio-rules" className="w-full text-left text-sm">
                                <tbody>
                                    {STUDIO_RULES.map(([rule, policy], i) => (
                                        <tr key={rule} className={`${i ? "border-t border-ink/15" : ""} ${i % 2 ? "bg-paper/70" : ""}`}>
                                            <th className="w-1/2 px-4 py-3 align-top font-display text-base tracking-wide text-forest sm:w-2/5">{rule}</th>
                                            <td className="px-4 py-3 text-ink/80">{policy}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <section data-testid="pay-it-forward" className="bg-mustard px-5 py-16 sm:px-10 sm:py-20">
                <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
                    <FadeUp>
                        <p className="font-hand text-2xl text-brick -rotate-2 sm:text-3xl">PAY IT FORWARD</p>
                        <h2 className="mt-1 max-w-xl font-display text-4xl leading-[0.95] tracking-wide text-ink sm:text-5xl">
                            {t("membership.pif_heading", "CAN YOU SPOT A NEIGHBOR?")}
                        </h2>
                        <p className="mt-4 max-w-xl font-semibold text-ink">{t("membership.pif_lead", "")}</p>
                        <p className="mt-2 max-w-xl text-ink/80">{t("membership.pif_body", "")}</p>
                        <p className="mt-4 font-hand text-2xl text-brick -rotate-1">{t("membership.pif_note", "")}</p>
                    </FadeUp>
                    <FadeUp delay={0.15}>
                        <button
                            onClick={() => checkout("payitforward")}
                            disabled={paying === "payitforward"}
                            data-testid="pif-cta-button"
                            className="btn-stamp !bg-brick !text-xl disabled:opacity-60"
                        >
                            {paying === "payitforward" ? "Passing the hat…" : t("membership.pif_cta", "+$10/mo — Pay It Forward")}
                        </button>
                    </FadeUp>
                </div>
            </section>

            <FadeUp className="mx-auto max-w-3xl px-5 py-14 text-center sm:px-10">
                <Spark className="mx-auto h-8 w-8 text-mustard" />
                <p className="mt-4 text-ink/80">{t("membership.pay_note", "")}</p>
                <p className="mt-4 font-hand text-2xl text-brick -rotate-1 sm:text-3xl">
                    every dollar goes straight into paint, lumber, and an unreasonable amount of coffee.
                </p>
            </FadeUp>
            <Marquee />
            <div id="newsletter">
                <NewsletterSection idPrefix="membership-newsletter" tone="forest" />
            </div>
        </>
    );
};

export default Membership;
